import { Form, Button, Card } from "react-bootstrap";
import AppLoader from 'components/general/AppLoader';
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useState, useEffect, useCallback } from "react"; // Added useCallback
import { EditorState, convertToRaw } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";

export default function AddNewModal({ getAnnouncements }) {
  const { user: authUser } = useSelector((x) => x.auth);

  const url = process.env.REACT_APP_URL;
  const [to, setTo] = useState("");
  const [title, setTitle] = useState("");
  const [shortDescription, setShortDescription] = useState(EditorState.createEmpty());
  const [description, setDescription] = useState(EditorState.createEmpty());
  const [saveLoading, setSaveLoading] = useState(false);
  const [showCard, setShowCard] = useState(false)
  const handleCloseCard = () => setShowCard(false)
  const handleShowCard = () => setShowCard(true)

  const handleSubmit = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      setSaveLoading(true);

      const shortDescriptionHtml = draftToHtml(convertToRaw(shortDescription.getCurrentContent()));
      const descriptionHtml = draftToHtml(convertToRaw(description.getCurrentContent()));

      // Validation for character limits
      if (to === "") {
        toast.error("Please select an audience.", { position: "bottom-left" });
        setSaveLoading(false);
        return;
      }

      if (title.length > 100) {
        toast.error("Title must be 100 characters or less.", { position: "bottom-left" });
        setSaveLoading(false);
        return;
      }
      if (title.length < 3) {
        toast.error("Title is required.", { position: "bottom-left" });
        setSaveLoading(false);
        return;
      }

      if (shortDescription.getCurrentContent().getPlainText().length > 200) {
        toast.error("Short description must be 200 characters or less.", { position: "bottom-left" });
        setSaveLoading(false);
        return;
      }
      if (shortDescription.getCurrentContent().getPlainText().length < 3) {
        toast.error("Short description is required.", { position: "bottom-left" });
        setSaveLoading(false);
        return;
      }

      if (description.getCurrentContent().getPlainText().length > 2500) {
        toast.error("Description must be 2500 characters or less.", { position: "bottom-left" });
        setSaveLoading(false);
        return;
      }
      if (description.getCurrentContent().getPlainText().length < 3) {
        toast.error("Description is required.", { position: "bottom-left" });
        setSaveLoading(false);
        return;
      }

      try {
        const response = await fetch(`${url}/announcement`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authUser.token}`,
          },
          body: JSON.stringify({
            to,
            title,
            shortDescription: shortDescriptionHtml,
            description: descriptionHtml,
            active: true,
          }),
        });

        if (response.status === 201) {
          const data = await response.json();
          toast.success(`Announcement ${data.title} added successfully`, {
            position: "bottom-left",
          });
          getAnnouncements(); // Refresh the announcements list
          handleCloseCard();
        } else {
          const errorData = await response.json();
          toast.error(`Failed to add announcement: ${errorData.error || "Unknown error"}`, {
            position: "bottom-left",
          });
        }
      } catch (error) {
        console.error("Error adding announcement:", error);
        toast.error("Failed to add announcement. Please try again later.", { position: "bottom-left" });
      } finally {
        setSaveLoading(false);
      }
    },
    [authUser.token, description, getAnnouncements, shortDescription, title, to, url]
  );

  // Add CTRL+S (or CMD+S) keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (showCard && !saveLoading) {
          handleSubmit(); // Call handleSubmit without an event object
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showCard, saveLoading, handleSubmit]); // Added handleSubmit to the dependency array

  return (
    <>
      {!showCard && (
        <Button
          variant="success"
          onClick={handleShowCard}
          className="mb-3 w-100 announcements-add-trigger"
        >
          <i className="bi bi-plus"></i> Add New Announcement
        </Button>
      )}

      {showCard && (
        <Card className="mb-3 mt-3 announcements-form-card" id="addRole" style={{ display: showCard ? 'block' : 'none' }}>
          <Form onSubmit={handleSubmit}>
            <Card.Header className="announcements-form-header">
              <Card.Title className="mb-0">Create Announcement</Card.Title>
            </Card.Header>
            <Card.Body className="announcements-form-body">
              <Form.Group controlId="formTo" className="mb-3">
                <Form.Label>Audience</Form.Label>
                <Form.Select
                  aria-label="To"
                  className="formInput"
                  name="to"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                >
                  <option value="">Select Audience</option>
                  <option value="Officer">Officers</option>
                  <option value="Member">Members</option>
                  <option value="Guest">Guests</option>
                  <option value="Public">Public</option>
                </Form.Select>
              </Form.Group>

              <Form.Group controlId="formTitle" className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter announcement title (max 100 characters)"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Form.Group>

              <Form.Group controlId="formShortDescription" className="mb-3">
                <Form.Label>Short Description</Form.Label>
                <Editor
                  editorState={shortDescription}
                  wrapperClassName="demo-wrapper"
                  toolbarClassName="toolbar-class"
                  editorClassName="editor-class"
                  onEditorStateChange={setShortDescription}
                  placeholder="Enter announcement short description (max 200 characters)"
                  toolbar={{
                    options: ['inline', 'blockType', 'list', 'textAlign', 'link'],
                    inline: {
                      options: ['bold', 'italic', 'underline'],
                    },
                    blockType: {
                      inDropdown: false,
                      options: ['Normal', 'H4', 'H5', 'H6'],
                    },
                    fontSize: {
                      visible: false,
                      options: [8, 9, 10, 11, 12, 14, 18],
                    },
                    list: {
                      inDropdown: false,
                      options: ['unordered', 'ordered'],
                    },
                    textAlign: {
                      inDropdown: false,
                    },
                    link: {
                      inDropdown: false,
                    },
                    embedded: {
                      inDropdown: false,
                    },
                  }}
                />
              </Form.Group>

              <Form.Group controlId="formDescription" className="mb-3">
                <Form.Label>Full Description</Form.Label>
                <Editor
                  editorState={description}
                  wrapperClassName="demo-wrapper"
                  toolbarClassName="toolbar-class"
                  editorClassName="editor-class"
                  onEditorStateChange={setDescription}
                  placeholder="Enter announcement full description (max 2500 characters)"
                  toolbar={{
                    options: ['inline', 'blockType', 'list', 'textAlign', 'link'],
                    inline: {
                      options: ['bold', 'italic', 'underline'],
                    },
                    blockType: {
                      inDropdown: false,
                      options: ['Normal', 'H4', 'H5', 'H6'],
                    },
                    fontSize: {
                      visible: false,
                      options: [8, 9, 10, 11, 12, 14, 18],
                    },
                    list: {
                      inDropdown: false,
                      options: ['unordered', 'ordered'],
                    },
                    textAlign: {
                      inDropdown: false,
                    },
                    link: {
                      inDropdown: false,
                    },
                    embedded: {
                      inDropdown: false,
                    },
                  }}
                />
              </Form.Group>

            </Card.Body>
            <Card.Footer className='d-flex justify-content-between announcements-form-footer'>
              {saveLoading
                ?
                <>
                  <AppLoader />
                  Adding Announcement ...
                </>
                :
                <>
                  <Button variant="success" type='submit' onClick={handleSubmit} className='me-2 announcements-action-btn announcements-action-btn--success'>
                    Add Announcement
                  </Button>
                  <Button variant="danger" onClick={handleCloseCard} className="announcements-action-btn announcements-action-btn--danger">
                    Cancel
                  </Button>
                </>
              }
            </Card.Footer>
          </Form>
        </Card>
      )}

    </>
  );

}